import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { migrosLogoDataUrl } from './migros-logo-data';
import type { FabricReportPDFData } from './FabricInspectionReportPDF';

// ─── Helpers ────────────────────────────────────────────────────────────────
const NILL = 'Nill';

const formatDate = (raw?: string | null) => {
  if (!raw) return NILL;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
};

const val = (v: any): string => {
  if (v === null || v === undefined) return NILL;
  const str = String(v).trim();
  return str.length === 0 ? NILL : str;
};

const compositionText = (comp: any): string => {
  if (Array.isArray(comp)) return comp.map((c: any) => `${c.percentage}% ${c.material}`).join(', ') || NILL;
  if (comp && typeof comp === 'object') return JSON.stringify(comp);
  const s = val(comp); return s;
};

const BORDER = '#000000';

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 32,
    paddingTop: 18,
    paddingBottom: 50,
  },

  topHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  logoImg: { width: 140, height: 26, objectFit: 'contain' },
  subTitle: { fontSize: 8.5, color: '#000000', marginTop: 6, marginBottom: 10 },

  bigSectionTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', marginVertical: 12, marginLeft: 18 },
  sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 12, marginBottom: 6, textDecoration: 'underline' },

  // Info table
  infoRow: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: BORDER, minHeight: 20 },
  infoLabelCell: { width: 180, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  infoLabelText: { fontSize: 9 },
  infoValueCell: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },
  infoValueText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  // Result row
  resultHeaderRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  resultHeaderLabel: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderLabelText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  resultHeaderCheckCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderCheckCellLast: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },

  // Signatures
  sigRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  sigCell: { flex: 1, padding: 6, borderRightWidth: 1, borderRightColor: BORDER, minHeight: 70 },
  sigCellLast: { flex: 1, padding: 6, minHeight: 70 },
  sigTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  sigField: { fontSize: 8.5, marginBottom: 2 },
  sigImage: { width: 90, height: 32, objectFit: 'contain', marginTop: 2 },

  // Data table
  dataTable: { borderWidth: 1, borderColor: BORDER, marginBottom: 6 },
  dataHeadRow: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: BORDER },
  dataBodyRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BORDER, minHeight: 20 },
  dataCell: { flex: 1, padding: 4, paddingHorizontal: 5, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center' },
  dataCellLast: { flex: 1, padding: 4, paddingHorizontal: 5, justifyContent: 'center' },
  dataCellText: { fontSize: 8.5 },
  dataCellTextBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  dataCellTextCenter: { fontSize: 8.5, textAlign: 'center' },
  dataCellTextHead: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },

  // Checkbox
  checkboxBoxLg: { width: 11, height: 11, borderWidth: 0.7, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
  checkboxMarkLg: { fontSize: 9, fontFamily: 'Helvetica-Bold', lineHeight: 1 },
  checkboxLabel: { fontSize: 9, marginLeft: 4 },
  checkboxItem: { flexDirection: 'row', alignItems: 'center', marginRight: 8 },

  // Remarks
  remarksBlock: { borderWidth: 1, borderColor: BORDER, padding: 6, minHeight: 26, marginBottom: 6 },
  remarksLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },

  // Photos
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  photoTile: { width: 162 },
  photoImg: { width: 162, height: 160, objectFit: 'cover' },
  photoCaption: { fontSize: 7.5, color: '#444', marginTop: 2 },

  // Footer
  pageFooter: {
    position: 'absolute', bottom: 14, left: 32, right: 32,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Sub-components ──────────────────────────────────────────────────────────
const PageTop = ({ logoSrc }: { logoSrc: string }) => (
  <>
    <View style={s.topHeader} fixed>
      <Image src={logoSrc} style={s.logoImg} />
    </View>
    <Text style={s.subTitle} fixed>FINALRANDOMINSPECTION(TEXTILE/HOMETEXTILE)Version-1</Text>
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft}>MIGROS Asia</Text>
    <Text style={s.footerCenter} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    <Text style={s.footerRight}>Fabric Inspection</Text>
  </View>
);

const Checkbox = ({ checked, label }: { checked?: boolean; label: string }) => (
  <View style={s.checkboxItem}>
    <View style={s.checkboxBoxLg}>{checked ? <Text style={s.checkboxMarkLg}>X</Text> : null}</View>
    <Text style={s.checkboxLabel}>{label}</Text>
  </View>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={s.infoRow}>
    <View style={s.infoLabelCell}><Text style={s.infoLabelText}>{label}</Text></View>
    <View style={s.infoValueCell}><Text style={s.infoValueText}>{value}</Text></View>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
const MigrosFabricInspectionReportPDF = ({ report, logoDataUrl }: { report: FabricReportPDFData; logoDataUrl?: string }) => {
  const logoSrc = logoDataUrl || migrosLogoDataUrl;
  const isPass = report.result === 'PASS';
  const inspectorName = val(report.inspector_name);

  const gsmSamples = report.gsm_samples || report.gsmSamples || [];
  let avgGsm = 0;
  if (gsmSamples.length > 0) {
    let total = 0; let count = 0;
    for (const gs of gsmSamples) {
      const v = Number(gs.original_gsm ?? gs.originalGsm ?? 0);
      if (v > 0) { total += v; count++; }
    }
    if (count > 0) avgGsm = total / count;
  }
  const targetGsm = Number(report.target_gsm) || 0;
  const gsmVariation = targetGsm > 0 ? ((avgGsm - targetGsm) / targetGsm) * 100 : 0;

  const tEpi = Number(report.target_epi || 0);
  const oEpi = Number(report.original_epi || 0);
  const tPpi = Number(report.target_ppi || 0);
  const oPpi = Number(report.original_ppi || 0);

  const ptsPerHundred = report.total_yards && report.width
    ? ((report.total_penalty_points * 3600) / (report.total_yards * (report.width || 60))).toFixed(1)
    : NILL;

  // Collect all photos for picture pages
  const allPhotos: { url: string; label?: string }[] = [];
  for (const p of report.photos || []) {
    if (p.image_url) allPhotos.push({ url: p.image_url, label: p.label || '' });
  }
  for (const u of report.roll_width_images || []) {
    if (u) allPhotos.push({ url: u, label: 'Roll Width' });
  }
  for (const u of report.bow_skew_images || []) {
    if (u) allPhotos.push({ url: u, label: 'Bow & Skew' });
  }
  for (const c of report.checklists || []) {
    for (const u of c.evidence_urls || []) {
      if (u) allPhotos.push({ url: u, label: `Checklist: ${c.title}` });
    }
  }
  for (const d of report.defects || []) {
    for (const u of d.image_urls || []) {
      if (u) allPhotos.push({ url: u, label: `Defect: ${d.type}` });
    }
  }

  return (
    <Document title={`Migros Fabric Inspection Report - ${report.po_number || report.id}`}>

      {/* ═══ PAGE 1 — COVER ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <View style={{ flexDirection: 'row', marginBottom: 8, gap: 16 }}>
          <Checkbox label="Factory Self Inspection" />
          <Checkbox checked label="Fabric Inspection" />
          <Checkbox label="Re-Inspection" />
        </View>

        {/* Info table */}
        <View style={{ borderWidth: 1, borderColor: BORDER, borderTopWidth: 0 }}>
          <InfoRow label="Inspection Date:" value={formatDate(report.created_at)} />
          <InfoRow label="Inspector Name:" value={inspectorName} />
          <InfoRow label="SAP No./Supplier Name:" value={val(report.supplier_name || report.supplier)} />
          <InfoRow label="Buyer / Client:" value={val(report.buyer_name)} />
          <InfoRow label={`${(report.inspection_basis || 'PO').toUpperCase()} Number:`} value={val(report.po_number)} />
          <InfoRow label="Style / Ref Number:" value={val(report.style_number)} />
          <InfoRow label="Fabric Type:" value={val(report.fabric_type_name || report.fabric_type)} />
          <InfoRow label="Color Shade:" value={val(report.color_shade)} />
          <InfoRow label="Composition:" value={compositionText(report.composition)} />
          <InfoRow label="Total Yards / Meters:" value={report.total_yards ? `${report.total_yards} yds` : NILL} />
          <InfoRow label="Lot Size:" value={val(report.lot_size)} />
          <InfoRow label="Sample Size:" value={val(report.sample_size)} />
          <InfoRow label="Rolls Inspected:" value={val(report.rolls_inspected)} />
        </View>

        {/* Inspection Result Pass/Fail row */}
        <View style={s.resultHeaderRow}>
          <View style={s.resultHeaderLabel}><Text style={s.resultHeaderLabelText}>INSPECTIONRESULT</Text></View>
          <View style={s.resultHeaderCheckCell}><Checkbox checked={isPass} label="PASSED" /></View>
          <View style={s.resultHeaderCheckCellLast}><Checkbox checked={!isPass} label="FAILED" /></View>
        </View>

        {/* Signature blocks */}
        <View style={s.sigRow}>
          <View style={s.sigCell}>
            <Text style={s.sigTitle}>INSPECTOR</Text>
            <Text style={s.sigField}>Name: {inspectorName}</Text>
            <Text style={s.sigField}>Signature:</Text>
            {report.qa_signature_url ? <Image src={report.qa_signature_url} style={s.sigImage} /> : null}
          </View>
          <View style={s.sigCellLast}>
            <Text style={s.sigTitle}>FACTORY REPRESENTATIVE</Text>
            <Text style={s.sigField}>Name:</Text>
            <Text style={s.sigField}>Signature:</Text>
            {report.factory_signature_url ? <Image src={report.factory_signature_url} style={s.sigImage} /> : null}
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — Fabric Specifications + Weight + EPI/PPI ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part1- Fabric Specifications</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Selvedge</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Length</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Width</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Cuttable Width</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>AQL Points</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(report.selvedge)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(report.length)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(report.width)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>
              {(Number(report.width) > 0) ? (Number(report.width) - Number(report.selvedge || 0)).toFixed(2) : NILL}
            </Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(report.aql_points)}</Text></View>
          </View>
        </View>

        <Text style={s.sectionTitle}>Part2- Weight Check (GSM)</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Target GSM</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Average GSM</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Variation (%)</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{targetGsm > 0 ? targetGsm : NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{avgGsm > 0 ? avgGsm.toFixed(2) : NILL}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{avgGsm > 0 ? `${gsmVariation > 0 ? '+' : ''}${gsmVariation.toFixed(2)}%` : NILL}</Text></View>
          </View>
        </View>

        {gsmSamples.length > 0 && (
          <View style={s.dataTable}>
            <View style={s.dataHeadRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample #</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample Size</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Original GSM</Text></View>
            </View>
            {gsmSamples.map((gs, i) => {
              const sSize = gs.sample_size ?? gs.sampleSize;
              const oGsm = Number(gs.original_gsm ?? gs.originalGsm ?? 0);
              return (
                <View key={i} style={s.dataBodyRow}>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{i + 1}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{sSize != null ? String(sSize) : NILL}</Text></View>
                  <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{oGsm > 0 ? `${oGsm}` : NILL}</Text></View>
                </View>
              );
            })}
          </View>
        )}

        <Text style={s.sectionTitle}>Part3- Ends & Picks</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>EPI Target</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>EPI Actual</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>PPI Target</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>PPI Actual</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Variation (%)</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{tEpi || NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{oEpi || NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{tPpi || NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{oPpi || NILL}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>
              {(tEpi > 0 || tPpi > 0) ? (() => { const v = Math.abs(tEpi > 0 ? ((oEpi - tEpi) / tEpi) * 100 : 0) > Math.abs(tPpi > 0 ? ((oPpi - tPpi) / tPpi) * 100 : 0) ? ((oEpi - tEpi) / tEpi) * 100 : ((oPpi - tPpi) / tPpi) * 100; return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`; })() : NILL}
            </Text></View>
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 3 — Dimension Verification + 4-Point Summary ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part4- Dimension Verification</Text>

        <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>Roll Width Samples</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample #</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>At (m)</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Value</Text></View>
          </View>
          {(report.roll_width_samples && report.roll_width_samples.length > 0) ? (
            report.roll_width_samples.map((r, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{i + 1}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{r.length_point != null ? `${r.length_point} m` : NILL}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{r.value != null ? `${r.value} ${r.unit || 'in'}` : NILL}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4, marginTop: 8 }}>Bow & Skew Samples</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample #</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>At (m)</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Value</Text></View>
          </View>
          {(report.bow_skew_samples && report.bow_skew_samples.length > 0) ? (
            report.bow_skew_samples.map((r, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{i + 1}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{r.length_point != null ? `${r.length_point} m` : NILL}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{r.value != null ? `${r.value} ${r.unit || 'in'}` : NILL}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <Text style={s.sectionTitle}>Part5- 4 Point Inspection Summary</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Total Yards</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Total Penalty Pts</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Avg Pts / 100 Sq. Yds</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Standard (Max 40 pts)</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(report.total_yards)} yds</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(report.total_penalty_points)} pts</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{ptsPerHundred}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{(report.total_penalty_points || 0) <= 40 ? 'PASS' : 'FAIL'}</Text></View>
          </View>
        </View>

        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Inspector's Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{val(report.overall_remarks)}</Text>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 4 — Checklist + Defect Log ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part6- Checklist Results</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={[s.dataCell, { flex: 3 }]}><Text style={s.dataCellTextHead}>Check Point</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Status</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Remarks</Text></View>
          </View>
          {(report.checklists || []).length > 0 ? (
            report.checklists.map((item, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={[s.dataCell, { flex: 3 }]}><Text style={s.dataCellText}>{item.title}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{(item.status || '').toUpperCase()}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellText}>{val(item.remarks)}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={[s.dataCell, { flex: 3 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <Text style={s.sectionTitle}>Part7- Defect Log</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Roll #</Text></View>
            <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextHead}>Defect Type</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Category</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Yard #</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Penalty Pts</Text></View>
          </View>
          {(report.defects || []).length > 0 ? (
            report.defects.map((d, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{d.roll_number}</Text></View>
                <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellText}>{val(d.type)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(d.category || d.defect_type)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(d.yard_number)}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{d.points}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextCenter}>NO DEFECTS DETECTED</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>00</Text></View>
            </View>
          )}
          {/* Totals row */}
          {(report.defects || []).length > 0 && (
            <View style={[s.dataBodyRow, { backgroundColor: '#f5f5f5' }]}>
              <View style={[s.dataCell, { flex: 4 }]}><Text style={[s.dataCellTextBold, { textAlign: 'right' }]}>Total Penalty Points</Text></View>
              <View style={s.dataCellLast}><Text style={[s.dataCellTextCenter, { fontFamily: 'Helvetica-Bold' }]}>{String(report.total_penalty_points || 0).padStart(2, '0')}</Text></View>
            </View>
          )}
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 5+ — Pictures ═══ */}
      {allPhotos.length === 0 ? (
        <Page size="A4" style={s.page}>
          <PageTop logoSrc={logoSrc} />
          <Text style={s.bigSectionTitle}>PICTURES</Text>
          <Text style={{ fontSize: 9, marginLeft: 18, color: '#444' }}>{NILL}</Text>
          <PageFooter />
        </Page>
      ) : (
        (() => {
          const pages: { url: string; label?: string }[][] = [];
          for (let i = 0; i < allPhotos.length; i += 6) pages.push(allPhotos.slice(i, i + 6));
          return pages.map((chunk, pi) => (
            <Page key={pi} size="A4" style={s.page}>
              <PageTop logoSrc={logoSrc} />
              {pi === 0 ? <Text style={s.bigSectionTitle}>PICTURES</Text> : null}
              <View style={s.photoGrid}>
                {chunk.map((ph, i) => (
                  <View key={i} style={s.photoTile}>
                    <Image src={ph.url} style={s.photoImg} />
                    {ph.label ? <Text style={s.photoCaption}>{ph.label}</Text> : null}
                  </View>
                ))}
              </View>
              <PageFooter />
            </Page>
          ));
        })()
      )}

    </Document>
  );
};

export default MigrosFabricInspectionReportPDF;
